import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, PageSizeType, Table } from 'design-system-web';
import { Alert } from 'components/lds';

import { buildCoverageHoursRowActions } from './coverageHoursRowActions';
import { formatCoverageHours } from './coverageHoursRowFormat';
import {
  buildCoverageHoursTableRows,
  coverageHoursSubRows,
  CoverageHoursTableRow,
} from './coverageHoursTableRows';
import { CoverageHoursEntry, CoverageHoursQueryError } from './useCoverageHours';

type Props = {
  entries: CoverageHoursEntry[];
  totalRows: number;
  loading: boolean;
  error?: CoverageHoursQueryError;
  canManage: boolean;
  page: number;
  pageSize: PageSizeType;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: PageSizeType) => void;
  onDeleteRow: (row: CoverageHoursTableRow) => void;
  isDeleteDisabled: (row: CoverageHoursTableRow) => boolean;
};

export const coverageHoursRowId = (row: CoverageHoursTableRow): string =>
  row.id;

export function CoverageHoursTable({
  entries,
  totalRows,
  loading,
  error,
  canManage,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDeleteRow,
  isDeleteDisabled,
}: Props) {
  const { t } = useTranslation();

  const rows = useMemo(() => buildCoverageHoursTableRows(entries), [entries]);

  const columns: Column<CoverageHoursTableRow>[] = useMemo(
    () => [
      {
        key: 'categoryIssue',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.categoryIssue'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursTableRow) =>
          row.isChild
            ? null
            : `${row.category} – ${
                row.issue ??
                t('web.admin.serviceRequest.settings.coverageHours.create.none')
              }`,
      },
      {
        key: 'team',
        header: t('web.admin.serviceRequest.settings.coverageHours.table.team'),
        renderCell: (_cell: unknown, row: CoverageHoursTableRow) =>
          row.isChild
            ? (row.teamName ??
              t(
                'web.admin.serviceRequest.settings.coverageHours.table.teamUnavailable'
              ))
            : null,
      },
      {
        key: 'coverageHours',
        header: t(
          'web.admin.serviceRequest.settings.coverageHours.table.coverageHours'
        ),
        renderCell: (_cell: unknown, row: CoverageHoursTableRow) =>
          row.isChild ? formatCoverageHours(row) : null,
      },
    ],
    [t]
  );

  const rowActions = useMemo(
    () =>
      canManage
        ? buildCoverageHoursRowActions(t, onDeleteRow, isDeleteDisabled)
        : undefined,
    [canManage, t, onDeleteRow, isDeleteDisabled]
  );

  if (error) {
    return (
      <Alert type="error">
        {t('web.admin.serviceRequest.settings.coverageHours.error.load')}
      </Alert>
    );
  }

  return (
    <Table<CoverageHoursTableRow>
      columns={columns}
      data={rows}
      getSubRows={coverageHoursSubRows}
      rowActions={rowActions}
      disableSorting
      isLoading={loading}
      getRowId={coverageHoursRowId}
      pagination="server"
      totalRows={totalRows}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      showColumnVisibility
      tableKey="serviceRequestSettings.coverageHoursTable"
      emptyMessage={t(
        canManage
          ? 'web.admin.serviceRequest.settings.coverageHours.empty'
          : 'web.admin.serviceRequest.settings.coverageHours.emptyReadOnly'
      )}
    />
  );
}
